<template>
  <div class="formulator__sliders">
    <formulator-slider
      :key="item ? item.value : 'no-item'"
      :name="name"
      :unit="unit"
      :min="effectiveMin"
      :max="effectiveMax"
      :interval="interval"
      :category="selectedCategory"
      v-model="amount"
      :info-modal="infoModal"
      :disabled="disableSlider"
      :class="{'formulator__sliders--disabled': disableSlider }"
    >
      <template v-slot:title>
        <slot name="title" />
      </template>
    </formulator-slider>
    <div class="select-group">
      <p class="select-group__title">&nbsp;</p>
      <ev-select
        v-model="item"
        :options="items"
      />
    </div>
  </div>
</template>
<script>
import evSelect from '../ev-select.vue';
import formulatorSlider from './formulator-slider.vue';
import sliderValues from './slider-values';

function normalizeSliderAmount(rawAmount, {
  item = null,
  fallbackMax = 0,
  fallbackMin = 0,
  interval = 1,
} = {}) {
  let amount = Number(rawAmount);
  if (!Number.isFinite(amount) || amount < 0) amount = 0;
  amount = sliderValues.round(amount);

  const configuredMax = item && item.maxPercentage != null
    ? Number(item.maxPercentage)
    : Number(fallbackMax);
  const max = Math.max(configuredMax, amount);
  const min = item && item.minPercentage != null
    ? Number(item.minPercentage)
    : Number(fallbackMin);

  if (amount > max) amount = max;
  if (amount < min) amount = min;
  if (sliderValues.isSmallInterval(min, Number(interval)) && amount > 0 && amount < 0.5) {
    amount = sliderValues.snapToSkipValue(amount, max);
  }

  return amount;
}

export default {
  components: {
    evSelect,
    formulatorSlider,
  },
  data() {
    const valueId = this.value && this.value.id;
    const item = this.items.find(entry => String(entry.value) === String(valueId)) || null;
    return {
      amount: normalizeSliderAmount(this.value && this.value.amount, {
        item,
        fallbackMax: this.max,
        fallbackMin: this.min,
        interval: this.interval,
      }),
      item,
      syncingFromValue: false,
    };
  },
  props: {
    min: {
      type: [Number, String],
      required: true,
    },
    max: {
      type: [Number, String],
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    unit: {
      type: String,
      required: true,
    },
    interval: {
      type: [Number, String],
      required: true,
    },
    infoModal: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    initial: {
      type: Number,
      default: 0,
    },
    updateOnSelect: {
      type: Boolean,
      default: true,
    },
    // Al cambiar de ítem mantiene el porcentaje elegido (acotado al máximo del
    // nuevo ítem) en vez de volver a 0 o al valor por defecto.
    keepAmountOnSelect: {
      type: Boolean,
      default: false,
    },
    disableSlider: {
      type: Boolean,
      default: false,
    },
    showCustomAmount: {
      type: Boolean,
      default: false,
    },
    customAmount: {
      type: Number,
      default: 0,
    },
    isIngredient: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    itemValue() {
      const valueObj = {
        amount: this.showCustomAmount ? this.customAmount : this.amount,
        id: this.item ? this.item.value : null,
      };
      if (this.isIngredient) {
        valueObj.ms = this.item ? this.item.ms : 0;
      }

      return valueObj;
    },
    configuredMax() {
      if (this.item && this.item.maxPercentage != null) {
        return Number(this.item.maxPercentage);
      }
      return Number(this.max);
    },
    // Si la fórmula hidratada supera el max comercial, el slider igual debe
    // poder posicionar el handle (si no, queda en 0 con el texto en 10%).
    effectiveMax() {
      return Math.max(this.configuredMax, Number(this.amount) || 0);
    },
    effectiveMin() {
      if (this.item && this.item.minPercentage != null) {
        return Number(this.item.minPercentage);
      }
      return Number(this.min);
    },
    range() {
      return this.effectiveMax - this.effectiveMin;
    },
    selectedCategory() {
      return this.item && this.item.category ? this.item.category : null;
    },
  },
  methods: {
    findItem(id) {
      if (id == null) return null;
      return this.items.find(item => String(item.value) === String(id)) || null;
    },
    // vue-slider en modo discreto (interval 0.1) busca el value con indexOf:
    // tiene que ser Number y caer en la grilla (0, 0.5, 0.6, ...), si no el
    // handle queda en 0 aunque el texto muestre el porcentaje.
    normalizeAmount(rawAmount, item = this.item) {
      return normalizeSliderAmount(rawAmount, {
        item,
        fallbackMax: this.max,
        fallbackMin: this.min,
        interval: this.interval,
      });
    },
    syncFromValue(val) {
      if (!val) return;
      this.syncingFromValue = true;
      this.item = this.findItem(val.id);
      this.amount = this.normalizeAmount(val.amount, this.item);
      this.$nextTick(() => {
        this.syncingFromValue = false;
      });
    },
    updateDefaultValue() {
      this.$store.dispatch('setUpdated', false);
      if (this.item && this.updateOnSelect) {
        // eslint-disable-next-line no-magic-numbers
        this.amount = this.normalizeAmount(
          (this.range * Number(this.item.defaultPercentage) / 100) + this.effectiveMin,
        );
      }
    },
    clampAmount() {
      this.amount = this.normalizeAmount(this.amount);
    },
    // effectiveMax deja pasar montos sobre el máximo (fórmula hidratada), así
    // que al cambiar de ítem hay que acotar explícitamente al nuevo máximo.
    keepAmountWithinMax() {
      this.$store.dispatch('setUpdated', false);
      this.amount = this.normalizeAmount(Math.min(Number(this.amount) || 0, this.configuredMax));
    },
    setInitial() {
      if (!this.item && this.items.length > this.initial) this.item = this.items[this.initial];
    },
  },
  mounted() {
    this.setInitial();
    this.amount = this.normalizeAmount(this.amount);
  },
  watch: {
    value: {
      deep: true,
      handler(val) {
        const currentId = this.item ? this.item.value : null;
        if (String(currentId) === String(val && val.id)
          && Number(this.amount) === Number(val && val.amount)) {
          return;
        }
        this.syncFromValue(val);
      },
    },
    item(newItem, oldItem) {
      if (this.syncingFromValue) return;
      if (this.keepAmountOnSelect && oldItem) {
        this.keepAmountWithinMax();

        return;
      }
      this.amount = 0;
      this.updateDefaultValue();
      this.clampAmount();
    },
    itemValue(val) {
      this.$emit('input', val);
    },
    items() {
      if (!this.item && this.value && this.value.id != null) {
        this.item = this.findItem(this.value.id);
        this.amount = this.normalizeAmount(this.value.amount, this.item);
        return;
      }
      this.setInitial();
    },
    showCustomAmount(val) {
      if (val) this.amount = this.customAmount;
    },
  },
};
</script>
